<template>
  <v-app>
    <v-container fluid>
      <v-row>
        <v-col cols="12">
          <v-expansion-panels v-model="panel">
            <v-expansion-panel>
              <v-expansion-panel-title color="blue-grey-lighten-4" class="panel-header">
                Statement of Cash Flows
              </v-expansion-panel-title>
              <v-expansion-panel-text>
                <!-- Date Filters -->
                <v-row class="py-2">
                  <v-col md="3" cols="12">
                    <v-date-input 
                      v-model="filters.from_date" 
                      label="From Date" 
                      density="compact" 
                      prepend-icon=""
                      prepend-inner-icon="mdi-calendar-start" 
                      variant="outlined" 
                      bg-color="grey-lighten-4" 
                      autocomplete="off"
                      clearable
                    />
                  </v-col>
                  <v-col md="3" cols="12">
                    <v-date-input 
                      v-model="filters.to_date" 
                      label="To Date" 
                      density="compact" 
                      prepend-icon=""
                      prepend-inner-icon="mdi-calendar-end" 
                      variant="outlined" 
                      bg-color="grey-lighten-4" 
                      autocomplete="off"
                      clearable
                    />
                  </v-col>
                  <v-col md="6" cols="12">
                    <div class="d-flex justify-start align-center" style="gap: 12px;">
                      <v-btn class="bg-brand w-15" type="submit" @click="loadCashFlowStatement">
                        <v-icon>mdi-magnify</v-icon>
                        Generate Report
                      </v-btn>
                      <v-btn class="bg-warning w-15" type="button" prepend-icon="mdi-refresh" @click="resetFilters">
                        Reset
                      </v-btn>
                    </div>
                  </v-col>
                </v-row>

                <!-- Export Buttons -->
                <v-row v-if="cashFlowData" class="py-2 mt-2">
                  <v-col cols="12">
                    <div class="d-flex justify-center">
                      <v-btn-group variant="outlined" density="compact" size="x-small" divided>
                        <v-btn prepend-icon="mdi mdi-download" variant="flat" color="success" @click="exportToExcel" :disabled="!cashFlowData">EXCEL</v-btn>
                        <v-btn prepend-icon="mdi mdi-download" variant="flat" color="red" @click="exportToPDF" :disabled="!cashFlowData">PDF</v-btn>
                      </v-btn-group>
                    </div>
                  </v-col>
                </v-row>

                <!-- Cash Flow Statement -->
                <v-card v-if="cashFlowData" class="mt-2">
                  <v-card-title class="bg-primary text-white text-center">
                    <div class="w-100">
                      <div class="text-h5 font-weight-bold">STATEMENT OF CASH FLOWS</div>
                      <div class="text-subtitle-1">FOR THE PERIOD FROM {{ formatDate(filters.from_date) }} TO {{ formatDate(filters.to_date) }}</div>
                    </div>
                  </v-card-title>

                  <v-card-text class="pa-0">
                    <div class="cash-flow-report">
                      <!-- Opening Balance -->
                      <div class="cf-section">
 
                      </div>

                      <!-- Operating Activities -->
                      <div class="cf-section" v-if="cashFlowData.sections.operating">
                        <div class="cf-section-header">{{ cashFlowData.sections.operating.name }}</div>
                        <div 
                          v-for="(transaction, index) in cashFlowData.sections.operating.transactions" 
                          :key="`op-${index}`" 
                          class="cf-row"
                        >
                          <div class="cf-description indent">{{ transaction.name }}</div>
                          <div class="cf-amount">{{ formatBalance(transaction.amount) }}</div>
                        </div>
                      </div>

                      <!-- Investing Activities -->
                      <div class="cf-section" v-if="cashFlowData.sections.investing">
                        <div class="cf-section-header">{{ cashFlowData.sections.investing.name }}</div>
                        <div 
                          v-for="(transaction, index) in cashFlowData.sections.investing.transactions" 
                          :key="`inv-${index}`" 
                          class="cf-row"
                        >
                          <div class="cf-description indent">{{ transaction.name }}</div>
                          <div class="cf-amount">{{ formatBalance(transaction.amount) }}</div>
                        </div>
                      </div>

                      <!-- Financing Activities -->
                      <div class="cf-section" v-if="cashFlowData.sections.financing">
                        <div class="cf-section-header">{{ cashFlowData.sections.financing.name }}</div>
                        <div 
                          v-for="(transaction, index) in cashFlowData.sections.financing.transactions" 
                          :key="`fin-${index}`" 
                          class="cf-row"
                        >
                          <div class="cf-description indent">{{ transaction.name }}</div>
                          <div class="cf-amount">{{ formatBalance(transaction.amount) }}</div>
                        </div>
                      </div>

                      <!-- Net Increase/Decrease -->
                      <div class="cf-section">
                        <div class="cf-row cf-net-change">
                          <div class="cf-description">{{ cashFlowData.net_increase_decrease.name }}</div>
                          <div class="cf-amount">{{ formatBalance(cashFlowData.net_increase_decrease.balance) }}</div>
                        </div>
                      </div>

                      <div class="cf-section">  
                        <div class="cf-row cf-opening">
                          <div class="cf-description">{{ cashFlowData.opening_balance.name }}</div>
                          <div class="cf-amount">{{ formatBalance(cashFlowData.opening_balance.balance) }}</div>
                        </div>
                      </div>

                      <!-- Closing Balance -->
                      <div class="cf-section">
                        <div class="cf-row cf-closing">
                          <div class="cf-description">{{ cashFlowData.closing_balance.name }}</div>
                          <div class="cf-amount">{{ formatBalance(cashFlowData.closing_balance.balance) }}</div>
                        </div>
                      </div>
                    </div>
                  </v-card-text>
                </v-card>

                <!-- No Data Message -->
                <v-card v-else-if="!loading && !cashFlowData" class="mt-2">
                  <v-card-text class="text-center py-8">
                    <v-icon size="64" color="grey">mdi-cash-flow</v-icon>
                    <div class="text-h6 mt-2">No Cash Flow Data Found</div>
                    <div class="text-body-2 text-grey">Please adjust your filters and try again</div>
                  </v-card-text>
                </v-card>

                <!-- Loading State -->
                <v-card v-if="loading" class="mt-2">
                  <v-card-text class="text-center py-8">
                    <v-progress-circular indeterminate color="primary" size="64"></v-progress-circular>
                    <div class="text-h6 mt-2">Loading Cash Flow Statement...</div>
                  </v-card-text>
                </v-card>
              </v-expansion-panel-text>
            </v-expansion-panel>
          </v-expansion-panels>
        </v-col>
      </v-row>
    </v-container>
  </v-app>
</template>

<script>
import { accountsService } from '@/services/accountsService'
import { mapGetters } from 'vuex'

export default {
  name: 'CashFlowStatement',
  data() {
    return {
      panel: [0],
      loading: false,
      cashFlowData: null,
      filters: {
        from_date: new Date().toISOString().split('T')[0],
        to_date: new Date().toISOString().split('T')[0]
      }
    }
  },
  computed: {
    ...mapGetters(['getBranchId'])
  },
  methods: {
    async loadCashFlowStatement() {
      this.loading = true
      try {
        const params = {
          from_date: this.filters.from_date,
          to_date: this.filters.to_date
        }
        
        const response = await accountsService.getCashFlow(params)
        if (response.success) {
          this.cashFlowData = response.data
          this.$toast.success('Cash flow statement generated successfully')
        } else {
          this.$toast.error(response.message || 'Failed to generate cash flow statement')
        }
      } catch (error) {
        console.error('Error loading cash flow statement:', error)
        this.$toast.error('Failed to load cash flow statement')
        this.cashFlowData = null
      } finally {
        this.loading = false
      }
    },

    resetFilters() {
      this.filters = {
        from_date: new Date().toISOString().split('T')[0],
        to_date: new Date().toISOString().split('T')[0]
      }
      this.cashFlowData = null
    },

    formatDate(date) {
      if (!date) return ''
      return new Date(date).toLocaleDateString('en-GB')
    },

    formatBalance(balance) {
      if (!balance) return '0.00'
      
      // Handle wrapped balance object with nature
      if (typeof balance === 'object' && balance.balance !== undefined) {
        const amt = parseFloat(balance.balance).toFixed(2)
        const nature = balance.nature || ''
        return nature ? `${amt} ${nature}` : amt
      }
      
      // Handle string format from API
      if (typeof balance === 'string') {
        if (balance.includes('(') && balance.includes(')')) {
          return balance
        }
        const num = parseFloat(balance.replace(/[(),]/g, ''))
        return isNaN(num) ? '0.00' : num.toFixed(2)
      }
      
      // Handle simple number
      return parseFloat(balance).toFixed(2)
    },

    exportToExcel() {
      if (!this.cashFlowData) return

      const data = [
        ['Statement of Cash Flows'],
        [`FOR THE PERIOD FROM ${this.formatDate(this.filters.from_date)} TO ${this.formatDate(this.filters.to_date)}`],
        [],
        ['Description', 'Amount'],
        []
      ]

      // Opening Balance
      data.push([this.cashFlowData.opening_balance.name, this.formatBalance(this.cashFlowData.opening_balance.balance)])
      data.push([])

      // Operating Activities
      if (this.cashFlowData.sections.operating) {
        data.push([this.cashFlowData.sections.operating.name])
        this.cashFlowData.sections.operating.transactions.forEach(t => {
          data.push([`  ${t.name}`, this.formatBalance(t.amount)])
        })
        data.push([])
      }

      // Investing Activities
      if (this.cashFlowData.sections.investing) {
        data.push([this.cashFlowData.sections.investing.name])
        this.cashFlowData.sections.investing.transactions.forEach(t => {
          data.push([`  ${t.name}`, this.formatBalance(t.amount)])
        })
        data.push([])
      }

      // Financing Activities
      if (this.cashFlowData.sections.financing) {
        data.push([this.cashFlowData.sections.financing.name])
        this.cashFlowData.sections.financing.transactions.forEach(t => {
          data.push([`  ${t.name}`, this.formatBalance(t.amount)])
        })
        data.push([])
      }

      // Net Increase/Decrease
      data.push([this.cashFlowData.net_increase_decrease.name, this.formatBalance(this.cashFlowData.net_increase_decrease.balance)])
      data.push([])

      // Closing Balance
      data.push([this.cashFlowData.closing_balance.name, this.formatBalance(this.cashFlowData.closing_balance.balance)])

      this.$exportTableToExcel(data, 'cash_flow_statement_' + this.$fileDate())
    },

    exportToPDF() {
      if (!this.cashFlowData) return

      const columns = ['Description', 'Amount']
      const rows = []

      // Opening Balance
      rows.push([this.cashFlowData.opening_balance.name, this.formatBalance(this.cashFlowData.opening_balance.balance)])
      rows.push(['', ''])

      // Operating Activities
      if (this.cashFlowData.sections.operating) {
        rows.push([{ content: this.cashFlowData.sections.operating.name, colSpan: 2, styles: { fontStyle: 'bold', fillColor: [240, 240, 240] } }])
        this.cashFlowData.sections.operating.transactions.forEach(t => {
          rows.push([`  ${t.name}`, this.formatBalance(t.amount)])
        })
        rows.push(['', ''])
      }

      // Investing Activities
      if (this.cashFlowData.sections.investing) {
        rows.push([{ content: this.cashFlowData.sections.investing.name, colSpan: 2, styles: { fontStyle: 'bold', fillColor: [240, 240, 240] } }])
        this.cashFlowData.sections.investing.transactions.forEach(t => {
          rows.push([`  ${t.name}`, this.formatBalance(t.amount)])
        })
        rows.push(['', ''])
      }

      // Financing Activities
      if (this.cashFlowData.sections.financing) {
        rows.push([{ content: this.cashFlowData.sections.financing.name, colSpan: 2, styles: { fontStyle: 'bold', fillColor: [240, 240, 240] } }])
        this.cashFlowData.sections.financing.transactions.forEach(t => {
          rows.push([`  ${t.name}`, this.formatBalance(t.amount)])
        })
        rows.push(['', ''])
      }

      // Net Increase/Decrease
      rows.push([{ content: this.cashFlowData.net_increase_decrease.name, styles: { fontStyle: 'bold' } }, { content: this.formatBalance(this.cashFlowData.net_increase_decrease.balance), styles: { fontStyle: 'bold' } }])
      rows.push(['', ''])

      // Closing Balance
      rows.push([{ content: this.cashFlowData.closing_balance.name, styles: { fontStyle: 'bold', fillColor: [220, 240, 220] } }, { content: this.formatBalance(this.cashFlowData.closing_balance.balance), styles: { fontStyle: 'bold', fillColor: [220, 240, 220] } }])

      this.$exportTableToPDF(
        columns,
        rows,
        `Statement of Cash Flows - FOR THE PERIOD FROM ${this.formatDate(this.filters.from_date)} TO ${this.formatDate(this.filters.to_date)}`,
        'cash_flow_statement_' + this.$fileDate(),
        'portrait',
        {
          styles: { fontSize: 10, cellPadding: 3 },
          headStyles: { fontSize: 11, fillColor: [66, 139, 202], fontStyle: 'bold' },
          columnStyles: {
            0: { cellWidth: 140, halign: 'left' },
            1: { cellWidth: 50, halign: 'right' }
          }
        }
      )
    }
  }
}
</script>

<style scoped>
.panel-header {
  font-weight: bold;
  font-size: 1.1rem;
}

.cash-flow-report {
  padding: 20px;
  background-color: white;
}

.cf-section {
  margin-bottom: 20px;
}

.cf-section-header {
  font-weight: bold;
  font-size: 14px;
  padding: 8px 12px;
  background-color: #e3f2fd;
  border-left: 4px solid #2196f3;
  margin-bottom: 8px;
}

.cf-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 12px;
  border-bottom: 1px solid #f0f0f0;
  font-size: 13px;
}

.cf-row:hover {
  background-color: #fafafa;
}

.cf-description {
  flex: 1;
  text-align: left;
}

.cf-description.indent {
  padding-left: 24px;
}

.cf-amount {
  min-width: 120px;
  text-align: right;
  
  font-weight: 500;
}

.cf-opening {
  
  font-weight: bold;
  
  margin-bottom: 10px;
}

.cf-net-change {
 
  font-weight: bold;
  
  margin-top: 10px;
}

.cf-closing {

  font-weight: bold;
  font-size: 14px;
  
  margin-top: 10px;
}

@media (max-width: 768px) {
  .cf-row {
    font-size: 12px;
    padding: 6px 8px;
  }
  
  .cf-amount {
    min-width: 100px;
  }
}
</style>

